import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal, TextInput, Theme } from '../../../components';
import { DepartmentTemplate, DepartmentTemplateField } from '../../../models';
import { departmentTemplatesSelectors } from '../../../redux';
import * as departmentTemplatesActions from '../../../redux/departmentTemplates/actions';

interface Props {
  departmentTemplate: DepartmentTemplate | null;
  isOpen: boolean;
  closeModal: () => void;
}

const AddEditDepartmentTemplateModal: FC<Props> = ({ departmentTemplate, isOpen, closeModal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isLoading = useSelector(departmentTemplatesSelectors.isAddEditLoading);
  const [departmentTemplateName, setDepartmentTemplateName] = useState<string>('');
  const [saveButtonPressed, setSaveButtonPressed] = useState<boolean>(false);

  const handleCloseModal = () => {
    setDepartmentTemplateName('');
    closeModal();
  };

  useEffect(() => {
    setDepartmentTemplateName(departmentTemplate?.name || '');
  }, [departmentTemplate, setDepartmentTemplateName]);

  useEffect(() => {
    if (saveButtonPressed && !isLoading) {
      dispatch(
        departmentTemplatesActions.getDepartmentTemplates({
          sort: [`${DepartmentTemplateField.CREATED_AT},ASC`],
        }),
      );
      handleCloseModal();
    }
  }, [saveButtonPressed, isLoading]);

  const saveDepartmentTemplate = () => {
    setSaveButtonPressed(true);
    if (departmentTemplate) {
      // Edit existing department template
      dispatch(
        departmentTemplatesActions.editDepartmentTemplate({
          ...departmentTemplate,
          name: departmentTemplateName,
        }),
      );
    } else {
      // Create a new department template
      dispatch(departmentTemplatesActions.addDepartmentTemplate(departmentTemplateName));
    }
  };

  return (
    <Modal
      title={t(departmentTemplate ? 'lblEditDepartment' : 'lblAddDepartment')}
      isOpen={isOpen}
      closeModal={() => closeModal()}>
      <div className='add-edit-container'>
        <TextInput
          label={t('lblDepartmentName')}
          placeholder='phDepartmentName'
          onChange={setDepartmentTemplateName}
          value={departmentTemplateName}
        />

        <div className='buttons'>
          <Button theme={Theme.INVERSE} onClickCallback={handleCloseModal}>
            {t('lblCancel')}
          </Button>
          <Button
            theme={Theme.SECONDARY}
            onClickCallback={saveDepartmentTemplate}
            isLoading={isLoading}
            isDisabled={!departmentTemplateName}>
            {t('lblSave')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEditDepartmentTemplateModal;
